import { Link } from "react-router-dom";
import { ArrowLeft, ExternalLink, Edit, Ban, CheckCircle2, Key, BarChart3, Mail, Phone, Globe, Building2, Calendar, CreditCard, Shield, Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";

const TENANT = {
  id: "org_nexus_002", name: "Nexus Financial", legalName: "Nexus Financial Services Pvt. Ltd.",
  industry: "Banking, Financial Services & Insurance", companySize: "1,001-5,000",
  country: "India", region: "ap-south-1", status: "Active", plan: "Enterprise", mrr: "$2,499",
  created: "Sep 3, 2025", lastActive: "5 minutes ago",
  contact: { name: "Rajesh Kumar", email: "rajesh@nexusfinancial.com", phone: "+91 98765 12345", title: "CTO" },
  billing: { email: "billing@nexusfinancial.com", address: "Tower B, Cyber City, Gurgaon, HR 122002", taxId: "29ABCDE1234F1Z5" },
  limits: { callsMonth: 10000000, rps: 500, rpm: 5000, bandwidthGB: 500, maxBytes: 1048576, maxKeys: 50 },
  usage: { callsUsed: 8432100, bandwidthUsed: 312, keysUsed: 12 },
  entropy: { sources: ["raw_qrng", "qrng_drbg", "vrf"], drbgAlgo: "AES-256-CTR", reseedInterval: 5000, expansion: "100x", dedicatedPool: true, poolSize: 128, assignedDevice: "QRNG-PROD-01" },
  security: { authMethod: "Bearer + mTLS", mfaEnforced: true, keyRotation: "90 days", ipWhitelist: ["10.0.0.0/8", "203.45.67.0/24", "192.168.1.100/32"], compliance: ["NIST SP 800-90A", "NIST SP 800-90B", "FIPS 140-2", "PCI DSS"] },
  webhook: { url: "https://nexusfinancial.com/webhooks/qrng", events: ["usage.threshold_reached", "api_key.revoked", "health.degraded", "security.anomaly_detected"] },
};

const ACTIVITY = [
  { time: "5 min ago", event: "API call to /v1/random/bytes", actor: "API Key: qe_live_nex_01" },
  { time: "12 min ago", event: "API call to /v1/vrf/generate", actor: "API Key: qe_live_nex_03" },
  { time: "1 hour ago", event: "Portal login", actor: "rajesh@nexusfinancial.com" },
  { time: "2 hours ago", event: "API key permissions updated", actor: "rajesh@nexusfinancial.com" },
  { time: "6 hours ago", event: "Usage threshold alert (80%)", actor: "System" },
  { time: "1 day ago", event: "New API key created: qe_live_nex_12", actor: "admin@nexusfinancial.com" },
];

const KEYS_SUMMARY = [
  { name: "Production Primary", key: "qe_live_nex_01", status: "active", lastUsed: "5 min ago", requests: "4,521,000" },
  { name: "Production Secondary", key: "qe_live_nex_02", status: "active", lastUsed: "10 min ago", requests: "2,100,000" },
  { name: "VRF Service", key: "qe_live_nex_03", status: "active", lastUsed: "12 min ago", requests: "1,200,000" },
  { name: "Staging", key: "qe_test_nex_01", status: "active", lastUsed: "3 hours ago", requests: "45,000" },
];

const InfoRow = ({ label, value, icon: Icon }) => (
  <div className="flex items-start gap-3 py-2.5 border-b border-slate-100 last:border-0">
    {Icon && <Icon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />}
    <div className="flex-1 min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
      <div className="text-sm text-slate-900 mt-0.5">{typeof value === 'string' ? value : value}</div>
    </div>
  </div>
);

export default function TenantDetail() {
  return (
    <div data-testid="tenant-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div className="flex items-center gap-4">
          <Link to="/admin/tenants" className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors" data-testid="back-to-tenants-btn">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight" data-testid="tenant-detail-title">{TENANT.name}</h1>
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] uppercase">{TENANT.status}</Badge>
              <Badge className="bg-slate-900 text-white text-[10px]">{TENANT.plan}</Badge>
            </div>
            <p className="text-sm text-slate-500 mt-0.5 font-mono">{TENANT.id}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/admin/tenants/org_nexus_002/usage" className="inline-flex items-center gap-2 border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors text-xs px-4 py-2" data-testid="view-usage-link">
            <BarChart3 className="w-3.5 h-3.5" /> View Usage
          </Link>
          <Link to="/admin/tenants/org_nexus_002/keys" className="inline-flex items-center gap-2 border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors text-xs px-4 py-2" data-testid="manage-keys-link">
            <Key className="w-3.5 h-3.5" /> Manage Keys
          </Link>
          <button className="inline-flex items-center gap-2 bg-slate-900 text-white hover:bg-slate-800 transition-colors text-xs font-bold uppercase tracking-widest px-4 py-2" data-testid="edit-tenant-btn">
            <Edit className="w-3.5 h-3.5" /> Edit
          </button>
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="bg-white border border-slate-200 h-10 p-0.5 mb-6 w-auto" data-testid="tenant-detail-tabs">
          <TabsTrigger value="overview" className="text-xs px-4 data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="detail-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="entropy" className="text-xs px-4 data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="detail-tab-entropy">Entropy Config</TabsTrigger>
          <TabsTrigger value="security" className="text-xs px-4 data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="detail-tab-security">Security</TabsTrigger>
          <TabsTrigger value="activity" className="text-xs px-4 data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="detail-tab-activity">Activity</TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview">
          <div className="grid lg:grid-cols-3 gap-4">
            {/* Org Info */}
            <div className="bg-white border border-slate-200 p-6" data-testid="tenant-org-info">
              <h3 className="text-xs uppercase tracking-[0.2em] text-slate-500 font-medium mb-4">Organization</h3>
              <InfoRow label="Legal Name" value={TENANT.legalName} icon={Building2} />
              <InfoRow label="Industry" value={TENANT.industry} />
              <InfoRow label="Company Size" value={TENANT.companySize} />
              <InfoRow label="Country" value={TENANT.country} icon={Globe} />
              <InfoRow label="Data Residency" value={TENANT.region} />
              <InfoRow label="Created" value={TENANT.created} icon={Calendar} />
              <InfoRow label="Last Active" value={TENANT.lastActive} icon={Clock} />
            </div>

            {/* Contact & Billing */}
            <div className="space-y-4">
              <div className="bg-white border border-slate-200 p-6" data-testid="tenant-contact-info">
                <h3 className="text-xs uppercase tracking-[0.2em] text-slate-500 font-medium mb-4">Primary Contact</h3>
                <InfoRow label="Name" value={TENANT.contact.name} />
                <InfoRow label="Email" value={TENANT.contact.email} icon={Mail} />
                <InfoRow label="Phone" value={TENANT.contact.phone} icon={Phone} />
                <InfoRow label="Title" value={TENANT.contact.title} />
              </div>
              <div className="bg-white border border-slate-200 p-6" data-testid="tenant-billing-info">
                <h3 className="text-xs uppercase tracking-[0.2em] text-slate-500 font-medium mb-4">Billing</h3>
                <InfoRow label="MRR" value={TENANT.mrr} icon={CreditCard} />
                <InfoRow label="Billing Email" value={TENANT.billing.email} />
                <InfoRow label="Tax ID" value={TENANT.billing.taxId} />
              </div>
            </div>

            {/* Usage Summary */}
            <div className="space-y-4">
              <div className="bg-white border border-slate-200 p-6" data-testid="tenant-usage-summary">
                <h3 className="text-xs uppercase tracking-[0.2em] text-slate-500 font-medium mb-4">Current Usage</h3>
                {[
                  { label: "API Calls", used: TENANT.usage.callsUsed, total: TENANT.limits.callsMonth },
                  { label: "Bandwidth (GB)", used: TENANT.usage.bandwidthUsed, total: TENANT.limits.bandwidthGB },
                  { label: "API Keys", used: TENANT.usage.keysUsed, total: TENANT.limits.maxKeys },
                ].map((m, i) => (
                  <div key={i} className="mb-4 last:mb-0">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-slate-600">{m.label}</span>
                      <span className="font-mono text-slate-500">{m.used.toLocaleString()} / {m.total.toLocaleString()}</span>
                    </div>
                    <Progress value={(m.used / m.total) * 100} className="h-1.5 bg-slate-100" />
                  </div>
                ))}
              </div>
              <div className="bg-white border border-slate-200 p-6" data-testid="tenant-limits-info">
                <h3 className="text-xs uppercase tracking-[0.2em] text-slate-500 font-medium mb-4">Rate Limits</h3>
                <InfoRow label="Max RPS" value={`${TENANT.limits.rps.toLocaleString()} req/sec`} />
                <InfoRow label="Max RPM" value={`${TENANT.limits.rpm.toLocaleString()} req/min`} />
                <InfoRow label="Max Bytes/Request" value={`${(TENANT.limits.maxBytes / 1024 / 1024).toFixed(0)} MB`} />
              </div>
              <div className="bg-white border border-slate-200 p-5" data-testid="tenant-keys-summary">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs uppercase tracking-[0.2em] text-slate-500 font-medium">API Keys ({KEYS_SUMMARY.length})</h3>
                  <Link to="/admin/tenants/org_nexus_002/keys" className="text-[10px] text-cyan-600 hover:text-cyan-700 uppercase tracking-wider font-medium" data-testid="view-all-keys-link">View All</Link>
                </div>
                {KEYS_SUMMARY.slice(0, 3).map((k, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
                    <div>
                      <span className="text-xs text-slate-900">{k.name}</span>
                      <span className="text-[10px] text-slate-400 ml-2 font-mono">{k.key.slice(0, 14)}...</span>
                    </div>
                    <span className="text-[10px] text-slate-500">{k.lastUsed}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Entropy Config */}
        <TabsContent value="entropy">
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="bg-white border border-slate-200 p-6" data-testid="tenant-entropy-config">
              <h3 className="text-xs uppercase tracking-[0.2em] text-slate-500 font-medium mb-4">Entropy Sources</h3>
              {TENANT.entropy.sources.map((s, i) => (
                <div key={i} className="flex items-center gap-2 py-2 border-b border-slate-100 last:border-0">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  <span className="text-sm text-slate-900 capitalize">{s.replace(/_/g, ' ')}</span>
                </div>
              ))}
              <div className="mt-4 pt-4 border-t border-slate-200">
                <InfoRow label="DRBG Algorithm" value={TENANT.entropy.drbgAlgo} />
                <InfoRow label="Reseed Interval" value={`${TENANT.entropy.reseedInterval.toLocaleString()} requests`} />
                <InfoRow label="Expansion Factor" value={TENANT.entropy.expansion} />
              </div>
            </div>
            <div className="bg-white border border-slate-200 p-6" data-testid="tenant-pool-config">
              <h3 className="text-xs uppercase tracking-[0.2em] text-slate-500 font-medium mb-4">Dedicated Entropy Pool</h3>
              <Badge className="bg-emerald-50 text-emerald-700 text-[10px] border border-emerald-200 mb-4">Enabled</Badge>
              <InfoRow label="Pool Size" value={`${TENANT.entropy.poolSize} MB`} />
              <InfoRow label="Assigned Device" value={TENANT.entropy.assignedDevice} />
            </div>
          </div>
        </TabsContent>

        {/* Security */}
        <TabsContent value="security">
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="bg-white border border-slate-200 p-6" data-testid="tenant-security-config">
              <h3 className="text-xs uppercase tracking-[0.2em] text-slate-500 font-medium mb-4">Authentication & Access</h3>
              <InfoRow label="Auth Method" value={TENANT.security.authMethod} icon={Shield} />
              <InfoRow label="MFA Enforced" value={TENANT.security.mfaEnforced ? "Yes" : "No"} />
              <InfoRow label="Key Rotation" value={TENANT.security.keyRotation} />
              <div className="mt-4 pt-4 border-t border-slate-200">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-2">IP Whitelist</div>
                <div className="space-y-1">
                  {TENANT.security.ipWhitelist.map((ip, i) => (
                    <code key={i} className="block text-xs font-mono text-slate-700 bg-slate-50 px-2 py-1 border border-slate-100">{ip}</code>
                  ))}
                </div>
              </div>
            </div>
            <div className="bg-white border border-slate-200 p-6" data-testid="tenant-compliance-config">
              <h3 className="text-xs uppercase tracking-[0.2em] text-slate-500 font-medium mb-4">Compliance Frameworks</h3>
              <div className="flex flex-wrap gap-2">
                {TENANT.security.compliance.map((c, i) => (
                  <Badge key={i} variant="outline" className="text-xs text-slate-600 border-slate-200 bg-slate-50">{c}</Badge>
                ))}
              </div>
              <div className="mt-6 pt-4 border-t border-slate-200">
                <h4 className="text-[10px] uppercase tracking-wider text-slate-400 mb-3">Webhook</h4>
                <code className="text-xs font-mono text-slate-700 bg-slate-50 px-2 py-1 border border-slate-100 block mb-2">{TENANT.webhook.url}</code>
                <div className="flex flex-wrap gap-1 mt-2">
                  {TENANT.webhook.events.map((e, i) => (
                    <Badge key={i} variant="outline" className="text-[9px] font-mono text-slate-500">{e}</Badge>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Activity */}
        <TabsContent value="activity">
          <div className="bg-white border border-slate-200" data-testid="tenant-activity-log">
          <div data-testid="tenant-activity-log">
            <div className="grid grid-cols-[140px_1fr_160px] bg-slate-50 border-b border-slate-200">
              {["Time","Event","Actor"].map(h => (
                <div key={h} className="px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</div>
              ))}
            </div>
            {ACTIVITY.map((a, i) => (
              <div key={i} className="grid grid-cols-[140px_1fr_160px] border-b border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid={`activity-row-${i}`}>
                <div className="px-4 py-3 text-xs text-slate-500 flex items-center">{a.time}</div>
                <div className="px-4 py-3 text-sm text-slate-900 flex items-center">{a.event}</div>
                <div className="px-4 py-3 text-xs text-slate-600 font-mono flex items-center">{a.actor}</div>
              </div>
            ))}
          </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
